import {
    useLayoutEffect,
    createContext,
    useCallback,
    useContext,
    forwardRef,
    useEffect,
    useState,
    useMemo,
    useRef,
    useId
} from "react";
import {
    type LayoutChangeEvent,
    Pressable,
    Animated,
    Platform,
    type View
} from "react-native";
import type IPopoverSurfaceProps from "./type";
import type {
    PopoverSurfaceNestedChildType,
    PopoverSurfaceWebPropsType,
    PopoverSurfaceNestingType,
    IPopoverSurfaceRef
} from "./type";
import stylesheet, {
    getPopoverSurfaceOrigin,
    useStyles
} from "./stylesheet";
import PopoverSurfaceEmpty from "./components/empty";
import PopoverSurfaceGroup from "./components/group";
import PopoverSurfaceHeading from "./components/heading";
import PopoverSurfaceList from "./components/list";
import PopoverSurfaceSearch from "./components/search";
import PopoverSurfaceSeparator from "./components/separator";
import PopoverSurfaceStatus from "./components/status";
import {
    usePopoverSurfaceMotion
} from "./hook";
import {
    POPOVER_SURFACE_FORWARDED_KEY,
    measurePopoverSurfaceAnchor,
    getIsPopoverSurfaceEditable,
    getPopoverSurfaceElement
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useOverlayAccessibility,
    useAnchoredPosition,
    POPOVER_PORTAL_NAME
} from "../../helpers/overlay";
import type {
    RefForwardingComponent
} from "../../types";
import {
    SURFACE_CONTAINER_COLOR
} from "../../helpers/theme/elevation";
import {
    Portal
} from "../../helpers/portalize";

const PopoverSurfaceNestingContext = createContext<PopoverSurfaceNestingType | null>(null);

const PopoverSurface: RefForwardingComponent<IPopoverSurfaceRef, IPopoverSurfaceProps> = ({
    onLayout: onLayoutProp,
    isAutoFocused = false,
    padding = "spacingXs",
    collisionPadding,
    align = "start",
    initialFocusRef,
    isModal = false,
    side = "bottom",
    returnFocusRef,
    customTheme,
    triggerRef,
    anchorRef,
    onDismiss,
    children,
    maxWidth,
    minWidth,
    onClosed,
    isOpen,
    offset,
    style,
    width,
    ...props
}, ref) => {
    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const parentNesting = useContext(PopoverSurfaceNestingContext);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const surfaceKey = useId();

    const [
        isMounted,
        setIsMounted
    ] = useState(false);

    const [
        anchorWidth,
        setAnchorWidth
    ] = useState<number | null>(null);

    const surfaceRef = useRef<View>(null);

    const openChildrenRef = useRef(new Map<string, PopoverSurfaceNestedChildType>());

    const isRendered = isOpen || isMounted;

    const isAnchorWidth = width === "anchor" || minWidth === "anchor";

    const {
        availableHeight,
        onContentLayout,
        availableWidth,
        isPositioned,
        actualAlign,
        actualSide,
        measure,
        left,
        top
    } = useAnchoredPosition({
        offset: offset ?? spaces.spacingXs,
        isActive: isRendered,
        collisionPadding,
        customTheme,
        anchorRef,
        align,
        side
    });

    const isReady = isPositioned && (!isAnchorWidth || anchorWidth !== null);

    const isShow = isOpen && isReady;

    const handleExited = () => {
        setIsMounted(false);

        onClosed?.();
    };

    const {
        opacity,
        motion
    } = usePopoverSurfaceMotion({
        onExited: handleExited,
        customTheme,
        actualSide,
        isRendered,
        isReady,
        isOpen
    });

    const {
        surface: surfaceDynamicStyle
    } = useStyles({
        minWidth: minWidth === "anchor" ? anchorWidth ?? undefined : minWidth,
        width: width === "anchor" ? anchorWidth ?? undefined : width,
        availableHeight,
        availableWidth,
        isPositioned,
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        maxWidth,
        borders,
        padding,
        isShow,
        colors,
        spaces
    });

    const isContainingTarget = (target: Node): boolean => {
        const surfaceElement = getPopoverSurfaceElement(surfaceRef.current);

        const anchorElement = getPopoverSurfaceElement(anchorRef.current);

        const triggerElement = getPopoverSurfaceElement(triggerRef?.current);

        if(surfaceElement?.contains(target) || anchorElement?.contains(target) || triggerElement?.contains(target)) {
            return true;
        }

        return Array.from(openChildrenRef.current.values()).some((child) => child.isContainingTarget(target));
    };

    const controllerRef = useRef({
        isContainingTarget,
        onDismiss
    });

    const nestingValue = useMemo<PopoverSurfaceNestingType>(() => ({
        onChildChange: (childKey, child) => {
            if(child) {
                openChildrenRef.current.set(childKey, child);

                return;
            }

            openChildrenRef.current.delete(childKey);
        }
    }), []);

    const {
        containerProps
    } = useOverlayAccessibility({
        containerRef: surfaceRef,
        isActive: isOpen,
        initialFocusRef,
        returnFocusRef,
        isAutoFocused,
        onDismiss,
        isModal
    });

    const handleSurfaceRef = useCallback((node: View | null) => {
        surfaceRef.current = node;

        if(typeof ref === "function") {
            ref(node);

            return;
        }

        if(ref) {
            ref.current = node;
        }
    }, [
        ref
    ]);

    useLayoutEffect(() => {
        controllerRef.current = {
            isContainingTarget,
            onDismiss
        };
    });

    useEffect(() => {
        if(isOpen) {
            setIsMounted(true);
        }
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(!isRendered) {
            return;
        }

        measure();

        if(!isAnchorWidth) {
            return;
        }

        measurePopoverSurfaceAnchor({
            onMeasure: (nextWidth) => {
                setAnchorWidth((previousWidth) => previousWidth === nextWidth ? previousWidth : nextWidth);
            },
            anchorRef
        });
    });

    useEffect(() => {
        if(!isOpen || Platform.OS !== "web" || typeof document === "undefined") {
            return;
        }

        const onPointerDown = (event: Event) => {
            const {
                target
            } = event;

            if(!(target instanceof Node) || controllerRef.current.isContainingTarget(target)) {
                return;
            }

            controllerRef.current.onDismiss?.();
        };

        document.addEventListener("pointerdown", onPointerDown, true);

        return () => {
            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [
        isOpen
    ]);

    useEffect(() => {
        const surfaceElement = getPopoverSurfaceElement(surfaceRef.current);

        if(!isShow || !surfaceElement) {
            return;
        }

        const onEditableKeyDown = (event: KeyboardEvent) => {
            if(event.defaultPrevented || event.isComposing || event.key !== POPOVER_SURFACE_FORWARDED_KEY || !getIsPopoverSurfaceEditable(event.target)) {
                return;
            }

            const forwardedEvent = new KeyboardEvent("keydown", {
                key: POPOVER_SURFACE_FORWARDED_KEY,
                cancelable: true,
                bubbles: true
            });

            surfaceElement.dispatchEvent(forwardedEvent);

            if(forwardedEvent.defaultPrevented) {
                event.preventDefault();
            }
        };

        surfaceElement.addEventListener("keydown", onEditableKeyDown, true);

        return () => {
            surfaceElement.removeEventListener("keydown", onEditableKeyDown, true);
        };
    }, [
        isShow
    ]);

    useEffect(() => {
        if(!isOpen || !parentNesting) {
            return;
        }

        parentNesting.onChildChange(surfaceKey, {
            isContainingTarget: (target) => controllerRef.current.isContainingTarget(target)
        });

        return () => {
            parentNesting.onChildChange(surfaceKey, null);
        };
    }, [
        parentNesting,
        surfaceKey,
        isOpen
    ]);

    const onSurfaceLayout = (event: LayoutChangeEvent) => {
        onLayoutProp?.(event);

        onContentLayout(event);
    };

    const webProps: PopoverSurfaceWebPropsType = Platform.OS === "web" ? {
        dataSet: {
            state: isOpen ? "open" : "closed",
            ncoreSurface: "popover",
            align: actualAlign,
            side: actualSide
        },
        dir: direction
    } : {};

    const renderBackdrop = () => {
        if(Platform.OS === "web" || !isOpen || !onDismiss) {
            return null;
        }

        return <Pressable
            importantForAccessibility="no"
            style={stylesheet.backdrop}
            onPress={onDismiss}
            accessible={false}
        />;
    };

    const renderSurface = () => {
        return <Animated.View
            {...props}
            {...containerProps}
            {...webProps}
            style={[
                stylesheet.surface,
                surfaceDynamicStyle,
                style,
                {
                    transformOrigin: getPopoverSurfaceOrigin({
                        actualAlign,
                        actualSide,
                        direction
                    }),
                    transform: [
                        {
                            translateX: left
                        },
                        {
                            translateY: top
                        },
                        {
                            translateX: motion.translateX
                        },
                        {
                            translateY: motion.translateY
                        },
                        {
                            scale: motion.scale
                        }
                    ],
                    opacity
                }
            ]}
            onLayout={onSurfaceLayout}
            ref={handleSurfaceRef}
        >
            <NCoreUIKitTheme.SurfaceProvider
                surfaceColor={SURFACE_CONTAINER_COLOR}
            >
                {children}
            </NCoreUIKitTheme.SurfaceProvider>
        </Animated.View>;
    };

    if(!isRendered) {
        return null;
    }

    return <Portal
        name={POPOVER_PORTAL_NAME}
    >
        <PopoverSurfaceNestingContext.Provider
            value={nestingValue}
        >
            {renderBackdrop()}
            {renderSurface()}
        </PopoverSurfaceNestingContext.Provider>
    </Portal>;
};
export default Object.assign(forwardRef(PopoverSurface), {
    Separator: PopoverSurfaceSeparator,
    Heading: PopoverSurfaceHeading,
    Search: PopoverSurfaceSearch,
    Status: PopoverSurfaceStatus,
    Empty: PopoverSurfaceEmpty,
    Group: PopoverSurfaceGroup,
    List: PopoverSurfaceList
});
